import type { ColorSource, Graphics as PixiGraphics } from "pixi.js";
import React, { useCallback, useEffect, useMemo } from "react";
import { along, distance, featureCollection, lineString, multiLineString, point } from "@turf/turf";
import { geoPath, GeoProjection } from "d3-geo";
import {
  colorNameMap,
  eramFontNameMap,
  eramSymbolFontNameMap,
  getBitmapTextStyles,
  useBCGs,
  useGeomaps,
} from "@poscon/shared-frontend";
import { FeatureCollection, Geometry, LineString, MultiLineString } from "geojson";
import { Coordinate } from "@poscon/shared-types";
import {
  GeomapLineProperties,
  GeomapTextFeature,
  DefaultTextProperties,
  GeomapSymbolFeature,
  DefaultSymbolProperties,
  MapObjectType,
  GeomapLineFeature,
  isGeomapPointFeature,
  isGeomapLineFeature,
  isGeomapTextFeature,
  isGeomapSymbolFeature,
  geomapSymbolMap,
} from "@poscon/shared-types/eram";

export const defaultLineStyle = {
  width: 1,
  alpha: 1,
  color: 0xf0f0f0,
  alignment: 1,
  pixelLine: true,
};

export const geomapTextFontName = eramFontNameMap[1];

const roundedPoint = (point: Coordinate) => [Math.round(point[0]), Math.round(point[1])] as const;

export type GeomapGeometryContainerProps = {
  featureCollection: FeatureCollection<Geometry, GeomapLineProperties>;
  color?: Uint8Array;
  projection: GeoProjection;
};
export const GeomapGeometryContainer = ({
  featureCollection,
  projection,
  color = colorNameMap.mediumGrey,
}: GeomapGeometryContainerProps) => {
  const draw = useCallback(
    (graphics: PixiGraphics) => {
      graphics.clear();
      const pathGen = geoPath(projection).context(graphics);
      for (const feature of featureCollection.features) {
        graphics.setStrokeStyle({
          ...defaultLineStyle,
          color,
        });
        pathGen(feature.geometry);
        graphics.stroke();
      }
    },
    [featureCollection, color, projection]
  );

  return <graphics roundPixels draw={draw} />;
};

export type GeomapTextContainerProps = {
  features: GeomapTextFeature[];
  defaultTextProperties?: DefaultTextProperties;
  scale: number;
  projection: GeoProjection;
  baseTint?: ColorSource;
};
export const GeomapTextContainer = ({
  features,
  defaultTextProperties,
  scale,
  projection,
  baseTint = colorNameMap.mediumGrey,
}: GeomapTextContainerProps) => {
  const bcgs = useBCGs();

  return (
    <>
      {features.map(({ geometry, properties }, i) => {
        const coord = geometry.coordinates as Coordinate;
        const p = roundedPoint(projection(coord)!);

        return (
          <bitmapText
            key={`${properties.textObjectId}-${i}Text`}
            label={`${properties.textObjectId}-${i}Text`}
            x={p[0] + (properties.xOffset ?? defaultTextProperties?.xOffset ?? 0) / scale}
            y={p[1] + (properties.yOffset ?? defaultTextProperties?.yOffset ?? 0) / scale}
            text={properties.text.join("\n").toUpperCase()}
            style={{ ...getBitmapTextStyles(geomapTextFontName), fill: baseTint }}
            scale={1 / scale}
          />
        );
      })}
    </>
  );
};

type GeomapSymbolContainerProps = {
  features: GeomapSymbolFeature[];
  defaultSymbolProperties?: DefaultSymbolProperties;
  defaultTextProperties?: DefaultTextProperties;
  scale: number;
  projection: GeoProjection;
};
const GeomapSymbolContainer = ({
  features,
  defaultSymbolProperties,
  defaultTextProperties,
  scale,
  projection,
}: GeomapSymbolContainerProps) => {
  const fontFamily = eramFontNameMap[1];
  const symbolFontName = eramSymbolFontNameMap[1];
  const tint = colorNameMap.mediumGrey;

  return (
    <>
      {features.map(({ geometry, properties }, i) => {
        const coord = geometry.coordinates as Coordinate;
        const p = roundedPoint(projection(coord)!);
        const style = properties.style ?? defaultSymbolProperties?.style;

        if (!style || !geomapSymbolMap[style]) {
          return null;
        }

        return (
          <container key={`${properties.symbolId}-${i}Symbol`} x={p[0]} y={p[1]}>
            <bitmapText
              roundPixels
              label={`${properties.symbolId}-${i}SymbolText`}
              text={geomapSymbolMap[style]}
              style={{ ...getBitmapTextStyles(symbolFontName), fill: tint }}
              scale={1 / scale}
            />
            {properties.text?.length && defaultTextProperties && (
              <bitmapText
                roundPixels
                x={(properties.xOffset ?? defaultTextProperties.xOffset) / scale}
                y={(properties.yOffset ?? defaultTextProperties.yOffset) / scale}
                label={`${properties.symbolId}-${i}Symbol`}
                text={properties.text.join("\n").toUpperCase()}
                style={{ ...getBitmapTextStyles(fontFamily), fill: tint }}
                scale={1 / scale}
              />
            )}
          </container>
        );
      })}
    </>
  );
};

export const geomapLineShortSpacing = 1;
export const geomapLineLongSpacing = 2;

type MapGroup = {
  mapGroupId?: number;
  mapObjectType?: MapObjectType;
  defaultSymbolProperties?: DefaultSymbolProperties;
  defaultTextProperties?: DefaultTextProperties;
  lineFeatures?: FeatureCollection<MultiLineString | LineString, GeomapLineProperties>;
  textFeatures?: GeomapTextFeature[];
  symbolFeatures?: GeomapSymbolFeature[];
};

type MapGroupContainerProps = { group: MapGroup; projection: GeoProjection; scale: number };

const MapGroupContainer = ({ group, projection, scale }: MapGroupContainerProps) => {
  const { defaultTextProperties, defaultSymbolProperties } = group;
  return (
    <>
      {group.lineFeatures && (
        <GeomapGeometryContainer featureCollection={group.lineFeatures} projection={projection} />
      )}
      {group.textFeatures && (
        <GeomapTextContainer
          features={group.textFeatures}
          defaultTextProperties={defaultTextProperties}
          projection={projection}
          scale={scale}
        />
      )}
      {group.symbolFeatures && (
        <GeomapSymbolContainer
          features={group.symbolFeatures}
          defaultSymbolProperties={defaultSymbolProperties}
          defaultTextProperties={defaultTextProperties}
          projection={projection}
          scale={scale}
        />
      )}
    </>
  );
};

const emptyArray: never[] = [];

type GeomapProps = {
  geomapId: string;
  filterGroups: number[];
  projection: GeoProjection;
  scale: number;
};
export const GeoMap = ({ geomapId, filterGroups, projection, scale }: GeomapProps) => {
  const geomaps = useGeomaps(geomapId, filterGroups);

  const mapGroups = useMemo(() => {
    const newGroups: MapGroup[] = [];
    for (const map of geomaps) {
      const { defaultLineProperties, defaultTextProperties, defaultSymbolProperties } = map.properties;
      const lineFeatures: GeomapLineFeature[] = [];
      const textFeatures: GeomapTextFeature[] = [];
      const symbolFeatures: GeomapSymbolFeature[] = [];
      for (const f of map.features) {
        if (
          isGeomapPointFeature(f) &&
          (f.properties.filterGroups ?? defaultLineProperties?.filterGroups ?? []).some((b) =>
            filterGroups.includes(b)
          )
        ) {
          symbolFeatures.push(
            point(f.geometry.coordinates, {
              symbolId: "",
              style: "OtherWaypoints",
            })
          );
        }
        if (
          isGeomapLineFeature(f) &&
          (f.properties.filterGroups ?? defaultLineProperties?.filterGroups ?? []).some((b) =>
            filterGroups.includes(b)
          )
        ) {
          // const lineStyle = defaultLineProperties?.style ?? f.properties.style ?? "Solid";
          // if (lineStyle !== "Solid") {
          //   const spacing = lineStyle === "LongDashed" ? geomapLineLongSpacing : geomapLineShortSpacing;
          //   const start = f.geometry.coordinates[0] as Coordinate;
          //   const end = f.geometry.coordinates[1] as Coordinate;
          //   const dist = distance(start, end, {
          //     units: "nauticalmiles",
          //   });
          //   const lineStr = lineString([start, end]);
          //   const newLines: number[][][] = [];
          //   let acc = 0;
          //   let j = 0;
          //   while (acc < dist) {
          //     const start = along(lineStr, acc, {
          //       units: "nauticalmiles",
          //     });
          //     let end: Feature<Point>;
          //     if (j % 2 === 0 && lineStyle === "LongDashShortDash") {
          //       end = along(lineStr, acc + geomapLineLongSpacing, {
          //         units: "nauticalmiles",
          //       });
          //       acc += geomapLineLongSpacing + spacing;
          //     } else {
          //       end = along(lineStr, acc + spacing, {
          //         units: "nauticalmiles",
          //       });
          //       acc += spacing * 2;
          //     }
          //     newLines.push([start.geometry.coordinates, end.geometry.coordinates]);
          //     j += 1;
          //   }
          //   lineFeatures.push(multiLineString(newLines, f.properties));
          // } else {
          lineFeatures.push(f);
          // }
        }
        if (
          isGeomapTextFeature(f) &&
          (f.properties.filterGroups ?? defaultTextProperties?.filterGroups ?? []).some((b) =>
            filterGroups.includes(b)
          )
        ) {
          textFeatures.push(f);
        }
        if (
          isGeomapSymbolFeature(f) &&
          (f.properties.filterGroups ?? defaultSymbolProperties?.filterGroups ?? []).some((b) =>
            filterGroups.includes(b)
          )
        ) {
          symbolFeatures.push(f);
        }
      }
      if (lineFeatures.length > 0) {
        newGroups.push({
          mapGroupId: map.properties.mapGroupId,
          mapObjectType: map.properties.mapObjectType,

          lineFeatures: featureCollection(lineFeatures),
        });
      }

      if (symbolFeatures.length > 0) {
        newGroups.push({
          mapGroupId: map.properties.mapGroupId,
          mapObjectType: map.properties.mapObjectType,

          symbolFeatures,
          defaultSymbolProperties: defaultSymbolProperties,
          defaultTextProperties: defaultTextProperties,
        });
      }
      // TODO: what determines text brightness?
      if (textFeatures.length > 0) {
        newGroups.push({
          mapGroupId: map.properties.mapGroupId,
          mapObjectType: map.properties.mapObjectType,

          textFeatures,
          defaultTextProperties: defaultTextProperties,
        });
      }
    }
    return newGroups;
  }, [filterGroups, geomaps]);

  return (
    <>
      {mapGroups.map((group, i) => {
        return <MapGroupContainer key={i} group={group} projection={projection} scale={scale} />;
      })}
    </>
  );
};
